import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { InspectorToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useInspectorMutations } from '~/hooks/use-inspectors';
import { inspectorSchema, EMPTY_INSPECTOR, tokenToForm, type InspectorFormValues } from '~/lib/schemas/inspector';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Row being edited, or null to add. */
	row: InspectorToken | null;
}

/** Add / edit an inspector (Επιθεωρητής). */
export function InspectorEditDialog({ open, onOpenChange, row }: Props) {
	const { t } = useTranslation();
	const { create, update } = useInspectorMutations();

	const form = useForm<InspectorFormValues>({
		resolver: zodResolver(inspectorSchema) as Resolver<InspectorFormValues>,
		defaultValues: row ? tokenToForm(row) : EMPTY_INSPECTOR,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(row?.id);

	useEffect(() => {
		if (open) reset(row ? tokenToForm(row) : EMPTY_INSPECTOR);
	}, [open, row, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync({ form: values, original: row });
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const fld = (key: string) => t(`customers.inspectors.fields.${key}`);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('customers.inspectors.editTitle') : t('customers.inspectors.addTitle')}</DialogTitle>
					<DialogDescription>{t('customers.inspectors.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={fld('name')} error={formState.errors.fn?.message}>
							<Input {...register('fn')} maxLength={50} aria-invalid={Boolean(formState.errors.fn)} autoFocus />
						</FormField>
						<FormField label={fld('surname')} error={formState.errors.ln?.message}>
							<Input {...register('ln')} maxLength={50} aria-invalid={Boolean(formState.errors.ln)} />
						</FormField>
						<FormField label={fld('email')} error={formState.errors.eml?.message}>
							<Input {...register('eml')} maxLength={255} />
						</FormField>
						<FormField label={fld('tel')} error={formState.errors.tel?.message}>
							<Input {...register('tel')} maxLength={20} />
						</FormField>
						<FormField label={fld('mobile')} error={formState.errors.mob?.message}>
							<Input {...register('mob')} maxLength={20} />
						</FormField>
						<FormField label={fld('sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} />
						</FormField>
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
						<Label htmlFor="insp-iv">{fld('visible')}</Label>
						<Switch id="insp-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
